import {
  CompanyRole,
  EmployeeStatusEnum,
  PermissionGroup,
  ProjectRole,
  SystemRole,
} from '@vedalib/crud'
import {
  ProgressDescriptionPosition,
  InitialOpen,
  AnimationTypeCardEnum,
  FlipMethodCardEnum,
  MenuPositionType,
  SidebarMenuType,
  ViewTabsElementType,
  PositionTabsElementType,
  SizeTabsElementType,
} from '@vedalib/editor/lib/brand/types'
import {
  CollapseIconPosition,
  ProgressIconPosition,
  ProgressType,
  TestFeedbackType,
  TestAnswerFeedbackDisplayOption,
  TestAnswerFeedbackSelectOption,
  CheckboxType,
  CheckboxCondition,
  CheckboxProgress,
  EmbeddedCompletionEnum,
} from '@vedalib/editor/lib/elements'
import { ObjectFitEnum } from '@vedalib/editor/lib/files'

import { ElementGroup } from 'components/Editor/EditorElements/elements.types'
import { VideoPointActionUiEnum } from 'components/Editor/EditorElements/elements/Media/mediaTypes'
import { ElementTableActionUiEnum } from 'components/Editor/EditorElements/elements/Table/components/TableDragControl/types'
import { NodeV2CommandsUiEnum } from 'components/Editor/NodeV2/enum'
import {
  BlockActionUiEnum,
  ColumnActionUiEnum,
  EditorActionUiEnum,
  PreviewBlockActionUiEnum,
} from 'components/Editor/types'
import { CommentSortUiEnum } from 'components/comments/enums'
import { CommentActionUiEnum } from 'components/comments/types'
import { PublicLinkEntityEnumUi } from 'components/forms/PublicLinkForm/type'
import { HubSpotOptionsEnumUi } from 'components/integrations/HubSpot/types'
import { UnsplashTableActionUiEnum } from 'components/pages/FinderPage/FinderFilePicker/Unsplash/types'
import { FinderPageActionUiEnum, FinderTableActionUiEnum } from 'components/pages/FinderPage/types'
import { SectionActionUiEnum } from 'components/sections/ProjectStructure/SectionToolbar/types'
import type { ValueType } from 'components/uiKit/KitTypes'
import type { IMenuOption } from 'components/uiKit/Menu'
import { SortUiEnum } from 'components/uiKit/SortPopover/types'
import { ViewResourceEnum } from 'components/uiKit/Table/TableView/types'
import {
  AvailabilityEnum,
  ContentSpacePublishStatus,
  ContractType,
  CourseProgressTypeEnum,
  ImageResolution,
  ImportBrandingStrategy,
  LicenseStatus,
  Locale,
  NotificationGroupEnum,
  PassOrderEnum,
  ResourcesLimit,
  ScormScoreType,
  ScormStatusMode,
  ScormVersionEnum,
  SuspendDataType,
  TaskStatus,
  VideoResolution,
  ProjectExportToPdfEvents,
  EmployeePreferencesTab,
  TranslateFormatEnum,
  ActiveAccounts,
  AiTextPromptTogglesEnum,
  AiProvider,
  ProjectAutoTranslateStatus,
  LmsIntegrationType,
  ProjectTranslateProvider,
  ScormBuildStatus,
  LmsTargetMode,
  WebExportScope,
  CompanyAuthStatusEnum,
  ExportAction,
  PdfFormatType,
  AutoTranslationStyleEnum,
  AiStoryboardVoiceAndToneEnum,
  AiStoryboardContentTransformationEnum,
  AiStoryboardKnowledgeCheckEnum,
  AnnounceTypeEnum,
  AnnounceStatuses,
  NavigationFooterMode,
} from 'gql/__generated__/graphql'
import { BlockModeEnum } from 'gql/__generated__/graphql'
import { DashboardProjectActionUiEnum } from 'routes/routes/App/routes/Company/pages/Dashboard/types'
import {
  BrandPageActionUiEnum,
  BrandTableActionUiEnum,
} from 'routes/routes/App/routes/Company/routes/Brands/pages/Brands/types'
import {
  CatalogPageActionUiEnum,
  CatalogTableActionUiEnum,
} from 'routes/routes/App/routes/Company/routes/Catalog/pages/Catalog/types'
import { ContentSpaceCourseTableActionUiEnum } from 'routes/routes/App/routes/Company/routes/ContentSpace/pages/ContentSpaceCoursesList/types'
import { ContentSpacePortalTableActionUiEnum } from 'routes/routes/App/routes/Company/routes/ContentSpace/pages/ContentSpacePortalsList/types'
import { EmployeeActionUi } from 'routes/routes/App/routes/Company/routes/Employees/pages/Employees/type'
import { ProjectExportActionUiEnum } from 'routes/routes/App/routes/Company/routes/Project/routes/Home/pages/Export/HistoryPage/types'
import { ProjectExportTabUiEnum } from 'routes/routes/App/routes/Company/routes/Project/routes/Home/pages/Export/type'
import {
  ParticipantsPageActionUiEnum,
  ParticipantsTableActionUiEnum,
} from 'routes/routes/App/routes/Company/routes/Project/routes/Home/pages/Participants/types'
import { ProjectLabelGroups } from 'routes/routes/App/routes/Company/routes/Project/routes/Home/routes/Translation/pages/Localization/helper'
import { TranslationTableActions } from 'routes/routes/App/routes/Company/routes/Project/routes/Home/routes/Translation/pages/TranlationTable/types'
import { ProjectTranslationTabEnumUi } from 'routes/routes/App/routes/Company/routes/Project/routes/Home/routes/Translation/types'
import {
  TagActionMenuEnum,
  TemplateTagEnum,
} from 'routes/routes/App/routes/Company/routes/Templates/components/TemplateTags/types'
import { TemplateActionsUiEnum } from 'routes/routes/App/routes/Company/routes/Templates/components/enum'
import { CollectionActionUiEnum } from 'routes/routes/App/routes/Company/routes/Templates/types'
import type { IUiPermission } from 'services/Permission/types'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import { AiConfigActionUiEnum } from '../entities/AiConfig/types'
import { UiTranslationStatus } from '../entities/AutoTranslations/types'
import { CompanyActionEnum } from '../entities/Compay/type'
import { LmsIntegrationActionUiEnum } from '../entities/LmsIntegration/types'
import {
  ExportTargetTypeUiEnum,
  ExportTypeEnumUi,
  ExportTypeUi,
  ManifestIdsTypeEnumUi,
  ScormFormControlsType,
} from '../entities/ProjectBuild/types'
import { AuthMethodEnum } from '../entities/Session/types'
import { AnnouncerTableActionUiEnum } from '../routes/routes/App/routes/Admin/routes/Announcers/Pages/Announcers/useTableActions'
import { throwLocal } from './dev'

export const TRANSLATE_ENUMS = {
  ActiveAccounts: ActiveAccounts,
  AvailabilityEnum: AvailabilityEnum,
  BlockModeEnum: BlockModeEnum,
  CollapseIconPosition: CollapseIconPosition,
  CompanyRole: CompanyRole,
  ContentSpacePublishStatus: ContentSpacePublishStatus,
  ContractType: ContractType,
  CourseProgressTypeEnum: CourseProgressTypeEnum,
  CheckboxType: CheckboxType,
  CheckboxCondition: CheckboxCondition,
  CheckboxProgress: CheckboxProgress,
  ContractExpirationWarning: ContractType,
  ContractExpired: ContractType,
  ElementGroup: ElementGroup,
  EmployeeStatusEnum: EmployeeStatusEnum,
  ImageResolution: ImageResolution,
  ImportBrandingStrategy: ImportBrandingStrategy,
  Locale: Locale,
  NotificationGroupEnum: NotificationGroupEnum,
  NotificationGroupEnum_info: NotificationGroupEnum,
  PassOrderEnum: PassOrderEnum,
  PermissionGroup: PermissionGroup,
  ProgressIconPosition: ProgressIconPosition,
  ProgressDescriptionPosition: ProgressDescriptionPosition,
  ProgressType: ProgressType,
  ProjectExportActionUiEnum: ProjectExportActionUiEnum,
  ProjectLabelGroups: ProjectLabelGroups,
  ProjectRole: ProjectRole,
  ScormScoreType: ScormScoreType,
  ScormStatusMode: ScormStatusMode,
  ScormVersionEnum: ScormVersionEnum,
  SectionTypeEnum: SectionTypeEnum,
  SectionTypeEnumPlaceholder: SectionTypeEnum,
  SuspendDataType: SuspendDataType,
  SystemRole: SystemRole,
  TaskStatus: TaskStatus,
  TestFeedbackType: TestFeedbackType,
  TemplateTagEnum: TemplateTagEnum,
  TestAnswerFeedbackDisplayOption: TestAnswerFeedbackDisplayOption,
  TestAnswerFeedbackSelectOption: TestAnswerFeedbackSelectOption,
  VideoResolution: VideoResolution,
  ResourcesLimit: ResourcesLimit,
  LicenseStatus: LicenseStatus,
  ProjectExportToPdfEvents: ProjectExportToPdfEvents,
  ScormBuildStatus: ScormBuildStatus,
  EmployeePreferencesTab: EmployeePreferencesTab,
  EmployeeActionUi: EmployeeActionUi,
  AnnounceTypeEnum: AnnounceTypeEnum,
  AnnounceStatuses: AnnounceStatuses,
  TranslateFormatEnum: TranslateFormatEnum,
  ExportTypeEnumUi: ExportTypeEnumUi,
  VideoPointActionUiEnum: VideoPointActionUiEnum,
  BrandTableActionUiEnum: BrandTableActionUiEnum,
  ContentSpaceCourseTableActionUiEnum: ContentSpaceCourseTableActionUiEnum,
  ContentSpacePortalTableActionUiEnum: ContentSpacePortalTableActionUiEnum,
  BrandPageActionUiEnum: BrandPageActionUiEnum,
  ElementTableActionUiEnum: ElementTableActionUiEnum,
  FinderPageActionUiEnum: FinderPageActionUiEnum,
  FinderTableActionUiEnum: FinderTableActionUiEnum,
  CatalogPageActionUiEnum: CatalogPageActionUiEnum,
  CatalogTableActionUiEnum: CatalogTableActionUiEnum,
  ParticipantsPageActionUiEnum: ParticipantsPageActionUiEnum,
  TagActionMenuEnum: TagActionMenuEnum,
  ParticipantsTableActionUiEnum: ParticipantsTableActionUiEnum,
  UnsplashTableActionUiEnum: UnsplashTableActionUiEnum,
  BlockActionUiEnum: BlockActionUiEnum,
  SectionActionUiEnum: SectionActionUiEnum,
  CommentActionUiEnum: CommentActionUiEnum,
  DashboardProjectActionUiEnum: DashboardProjectActionUiEnum,
  CollectionActionUiEnum: CollectionActionUiEnum,
  SortUiEnum: SortUiEnum,
  HubSpotOptionsEnumUi: HubSpotOptionsEnumUi,
  PublicLinkEntityEnumUi: PublicLinkEntityEnumUi,
  ManifestIdsTypeEnumUi: ManifestIdsTypeEnumUi,
  SidebarMenuType: SidebarMenuType,
  MenuPositionType: MenuPositionType,
  InitialOpen: InitialOpen,
  ProjectExportTabUiEnum: ProjectExportTabUiEnum,
  AiTextPromptTogglesEnum: AiTextPromptTogglesEnum,
  TemplateActionsUiEnum: TemplateActionsUiEnum,
  ColumnActionUiEnum: ColumnActionUiEnum,
  EditorActionUiEnum: EditorActionUiEnum,
  PreviewBlockActionUiEnum: PreviewBlockActionUiEnum,
  ProjectBuildActionUiEnum: ProjectExportActionUiEnum,
  ExportTypeUi: ExportTypeUi,
  AiProvider: AiProvider,
  AiConfigActionUiEnum: AiConfigActionUiEnum,
  ProjectAutoTranslateStatus: ProjectAutoTranslateStatus,
  CommentSortUiEnum: CommentSortUiEnum,
  LmsIntegrationType: LmsIntegrationType,
  LmsIntegrationActionUiEnum: LmsIntegrationActionUiEnum,
  ExportTargetTypeUiEnum: ExportTargetTypeUiEnum,
  AnimationTypeCardEnum: AnimationTypeCardEnum,
  FlipMethodCardEnum: FlipMethodCardEnum,
  ViewTabsElementType: ViewTabsElementType,
  PositionTabsElementType: PositionTabsElementType,
  SizeTabsElementType: SizeTabsElementType,
  ProjectTranslateProvider: ProjectTranslateProvider,
  ObjectFitEnum: ObjectFitEnum,
  LmsTargetMode: LmsTargetMode,
  ScormFormControlsType: ScormFormControlsType,
  TranslationTableActions: TranslationTableActions,
  ProjectTranslationTabEnumUi: ProjectTranslationTabEnumUi,
  UiTranslationStatus: UiTranslationStatus,
  ViewResourceEnum: ViewResourceEnum,
  AnnouncerTableActionUiEnum: AnnouncerTableActionUiEnum,
  WebExportScope: WebExportScope,
  CompanyActionEnum: CompanyActionEnum,
  CompanyAuthStatusEnum: CompanyAuthStatusEnum,
  AuthMethodEnum: AuthMethodEnum,
  ExportAction: ExportAction,
  ExportActionTypeDescription: ExportAction,
  NavigationFooterMode: NavigationFooterMode,
  PdfFormatType: PdfFormatType,
  EmbeddedCompletionEnum: EmbeddedCompletionEnum,
  AutoTranslationStyleEnum: AutoTranslationStyleEnum,
  NodeV2CommandsUiEnum: NodeV2CommandsUiEnum,
  AiStoryboardVoiceAndToneEnum: AiStoryboardVoiceAndToneEnum,
  AiStoryboardContentTransformationEnum: AiStoryboardContentTransformationEnum,
  AiStoryboardKnowledgeCheckEnum: AiStoryboardKnowledgeCheckEnum,
} as const

export type EnumName = keyof typeof TRANSLATE_ENUMS

export const getEnumLabel = <N extends EnumName, V = (typeof TRANSLATE_ENUMS)[N]>(
  enumName: N,
  enumValue: V,
  data?: Record<string, string | number | null | undefined>,
) => {
  const tKey = `enums.${enumName}.${enumValue}`
  const label = t(tKey, data)
  if (label === tKey) {
    throwLocal(`Add label to translate "enums.${enumName}.${enumValue}": "${enumValue}"  not found`)
  }

  return label
}

/**
 * Retrieves the option from an enum based on the enum name and enum value.
 * @example
 * ```typescript
 * enum SortUiEnum {
 *  CREATED_AT_ASC = 'CREATED_AT_ASC',
 *  CREATED_AT_DESC = 'CREATED_AT_DESC'
 * }
 * const OPTIONS = [
 *  getEnumOption('SortUiEnum', SortUiEnum.CREATED_AT_ASC),
 *  getEnumOption('SortUiEnum', SortUiEnum.CREATED_AT_DESC),
 * ]
 * // OPTIONS: [
 * //   { value: SortUiEnum.CREATED_AT_ASC, label: enums.SortUiEnum.CREATED_AT_ASC },
 * //   { value: SortUiEnum.CREATED_AT_DESC, label: enums.SortUiEnum.CREATED_AT_DESC }
 * // ]
 * ```
 */
export function getEnumOption<N extends EnumName, V = (typeof TRANSLATE_ENUMS)[N]>(
  enumName: N,
  enumValue: V,
): { value: V; label: string }
/**
 * Retrieves the option from an enum based on the enum name, enum value, and additional option data.
 * ```typescript
 * enum SortUiEnum {
 *  CREATED_AT_ASC = 'CREATED_AT_ASC',
 *  CREATED_AT_DESC = 'CREATED_AT_DESC'
 * }
 * const OPTIONS = [
 *  getEnumOption('SortUiEnum', SortUiEnum.CREATED_AT_ASC, {
 *    disabled: true,
 *    permission: {
 *      hidden: PermissionsKey.project_resource_d
 *    },
 *  }),
 *  getEnumOption('SortUiEnum', SortUiEnum.CREATED_AT_DESC, {
 *    hidden: true,
 *  }),
 * ]
 * // OPTIONS: [
 * //   {
 * //     value: SortUiEnum.CREATED_AT_ASC,
 * //     label: enums.SortUiEnum.CREATED_AT_ASC,
 * //     disabled: true,
 * //     permission: {
 * //       hidden: PermissionsKey.project_resource_d
 * //     }
 * //   },
 * //   {
 * //     value: SortUiEnum.CREATED_AT_DESC,
 * //     label: enums.SortUiEnum.CREATED_AT_DESC,
 * //     hidden: true
 * //   }
 * // ]
 * ```
 */
export function getEnumOption<
  N extends EnumName,
  O extends
    | Partial<IMenuOption<V extends ValueType ? V : ValueType> & IUiPermission>
    | Partial<IMenuOption<V extends ValueType ? V : ValueType>>,
  V = (typeof TRANSLATE_ENUMS)[N],
>(
  enumName: N,
  enumValue: V,
  option: O,
): O extends IUiPermission
  ? IMenuOption<V extends ValueType ? V : ValueType> & IUiPermission
  : IMenuOption<V extends ValueType ? V : ValueType>
export function getEnumOption<
  N extends EnumName,
  O extends
    | Partial<IMenuOption<V extends ValueType ? V : ValueType> & IUiPermission>
    | Partial<IMenuOption<V extends ValueType ? V : ValueType>>,
  V = (typeof TRANSLATE_ENUMS)[N],
>(
  enumName: N,
  enumValue: V,
  option?: O,
  data?: Record<string, string | number | null | undefined>,
): O extends IUiPermission
  ? IMenuOption<V extends ValueType ? V : ValueType> & IUiPermission
  : IMenuOption<V extends ValueType ? V : ValueType>

export function getEnumOption<N extends EnumName, V = (typeof TRANSLATE_ENUMS)[N]>(
  enumName: N,
  enumValue: V,
  option?:
    | Partial<IMenuOption<V extends ValueType ? V : ValueType> & IUiPermission>
    | Partial<IMenuOption<V extends ValueType ? V : ValueType>>,
  data?: Record<string, string | number | null | undefined>,
) {
  return { label: getEnumLabel(enumName, enumValue, data), value: enumValue, ...option }
}

export const toEnum = <E extends Record<string, string | number>>(
  value?: string | number | null,
  e?: E,
): E[keyof E] | undefined => {
  if (!e) {
    return undefined
  }
  // @ts-expect-error util
  if (Object.values(e).includes(value)) {
    return value as E[keyof E]
  }
  return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const enumValues = (obj: any) => Object.values(obj) as string[]
